import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import Button from '@components/Button';
import {
  listMusicTracks,
  createMusicTrack,
  updateMusicTrack,
  deleteMusicTrack,
  type NewMusicTrack,
} from '@lib/musicTracks';
import type { MusicTrack } from '@lib/api';

interface MusicFormState {
  name: string;
  audio_url: string;
  is_active: boolean;
}

const blankForm: MusicFormState = {
  name: '',
  audio_url: '',
  is_active: false,
};

const sortByName = (list: MusicTrack[]) =>
  [...list].sort((a, b) => a.name.localeCompare(b.name));

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

function Modal({ title, onClose, children }: ModalProps) {
  const titleId = useId();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-xl rounded-2xl bg-white/95 p-6 shadow-2xl backdrop-blur"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-xl font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <span className="sr-only">Close</span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6l12 12M6 18L18 6"
              />
            </svg>
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref as any} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminMusic() {
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTrack, setActiveTrack] = useState<MusicTrack | null>(null);
  const [formValues, setFormValues] = useState<MusicFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [playingPreview, setPlayingPreview] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadTracks() {
      try {
        setLoading(true);
        const data = await listMusicTracks(true);
        if (!cancelled) {
          setTracks(sortByName(data));
          setError(null);
        }
      } catch (_error) {
        if (!cancelled) {
          setError('Unable to load music tracks. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadTracks();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTextChange =
    (field: 'name' | 'audio_url') => (event: ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleActiveChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, is_active: event.target.checked }));
  };

  const openCreateModal = () => {
    setActiveTrack(null);
    setFormValues(blankForm);
    setFormError(null);
    setShowFormModal(true);
  };

  const openEditModal = (track: MusicTrack) => {
    setActiveTrack(track);
    setFormValues({
      name: track.name,
      audio_url: track.audio_url,
      is_active: Boolean(track.is_active),
    });
    setFormError(null);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setIsSubmitting(false);
  };

  const submitTrack = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    const trimmedName = formValues.name.trim();
    const trimmedAudioUrl = formValues.audio_url.trim();

    if (!trimmedName) {
      setFormError('Name is required.');
      return;
    }

    if (!trimmedAudioUrl) {
      setFormError('Audio URL is required.');
      return;
    }

    const payload: NewMusicTrack = {
      name: trimmedName,
      audio_url: trimmedAudioUrl,
      is_active: formValues.is_active,
    };

    setIsSubmitting(true);

    try {
      if (activeTrack) {
        const updated = await updateMusicTrack(activeTrack.id, payload);
        setTracks((prev) =>
          sortByName(
            prev.map((item) => (item.id === updated.id ? updated : item))
          )
        );
      } else {
        const created = await createMusicTrack(payload);
        setTracks((prev) => sortByName([...prev, created]));
      }
      setShowFormModal(false);
      setActiveTrack(null);
      setFormValues(blankForm);
    } catch (_error) {
      setFormError('Unable to save music track. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (track: MusicTrack) => {
    setActiveTrack(track);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    if (!activeTrack) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteMusicTrack(activeTrack.id);
      setTracks((prev) => prev.filter((item) => item.id !== activeTrack.id));
      setShowDeleteModal(false);
      setActiveTrack(null);
    } catch (_error) {
      setDeleteError('Failed to delete music track. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const togglePreview = (url: string) => {
    if (playingPreview === url) {
      audioRef.current?.pause();
      setPlayingPreview(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().catch(() => {
          console.error('Failed to play preview');
          setPlayingPreview(null);
        });
        setPlayingPreview(url);
      }
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100">
      <audio ref={audioRef} onEnded={() => setPlayingPreview(null)} />
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-purple-200/30 via-indigo-200/20 to-pink-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-5xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Music Management (Admin)
          </h1>
          <p className="text-lg text-gray-600">
            Manage the music tracks available to sessions.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">
                {tracks.length}
              </span>{' '}
              total
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {tracks.filter((t) => t.is_active).length}
              </span>{' '}
              active
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {Math.max(
                  0,
                  tracks.length - tracks.filter((t) => t.is_active).length
                )}
              </span>{' '}
              inactive
            </div>
          </div>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex justify-start">
            <Button
              type="button"
              onClick={openCreateModal}
              className="!bg-indigo-500 !text-white shadow-md transition hover:!bg-indigo-600 focus:!ring-indigo-400"
            >
              + New track
            </Button>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-28 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : tracks.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-indigo-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No music tracks yet. Create the first one to get started.
              </div>
            ) : (
              <>
                <div className="space-y-4 divide-y divide-white/40 lg:hidden">
                  {tracks.map((track) => (
                    <div
                      key={track.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            {track.name}
                          </h2>
                          <div className="mt-1 max-w-xs truncate text-xs text-gray-500">
                            {track.audio_url}
                          </div>
                          <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-gray-700 sm:grid-cols-2">
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Status
                              </span>
                              <div className="mt-1">
                                <span
                                  className={
                                    `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ` +
                                    (track.is_active
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-rose-100 text-rose-800')
                                  }
                                >
                                  {track.is_active ? 'Active' : 'Not active'}
                                </span>
                              </div>
                            </div>
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Updated
                              </span>
                              {formatDate(track.updated_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                          <button
                            onClick={() => togglePreview(track.audio_url)}
                            className="rounded-full p-2 text-indigo-600 hover:bg-gray-100"
                            title="Preview track"
                          >
                            {playingPreview === track.audio_url ? (
                              <svg
                                className="h-5 w-5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                              </svg>
                            ) : (
                              <svg
                                className="h-5 w-5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            )}
                          </button>
                          <ActionsMenu
                            id={track.id}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => openEditModal(track),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => openDeleteModal(track),
                                className: '!text-rose-700',
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Audio URL</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Updated</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tracks.map((track) => (
                          <tr
                            key={track.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top font-semibold text-gray-900">
                              {track.name}
                            </td>
                            <td className="max-w-xs truncate px-4 py-4 align-top text-gray-600">
                              {track.audio_url}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${track.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                              >
                                {track.is_active ? 'Active' : 'Not active'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {formatDate(track.updated_at)}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex flex-wrap items-center justify-end gap-3">
                                <button
                                  onClick={() => togglePreview(track.audio_url)}
                                  className="rounded-full p-2 text-indigo-600 transition-colors hover:bg-indigo-100"
                                  title="Preview track"
                                >
                                  {playingPreview === track.audio_url ? (
                                    <svg
                                      className="h-5 w-5"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="h-5 w-5"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  )}
                                </button>
                                <ActionsMenu
                                  id={track.id}
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () => openEditModal(track),
                                      className: '!text-indigo-700',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => openDeleteModal(track),
                                      className: '!text-rose-700',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showFormModal && (
        <Modal
          title={activeTrack ? 'Edit Music Track' : 'Create Music Track'}
          onClose={closeFormModal}
        >
          <form onSubmit={submitTrack} className="space-y-6">
            <div>
              <label
                htmlFor="music-name"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Music Name
              </label>
              <input
                id="music-name"
                type="text"
                value={formValues.name}
                maxLength={120}
                onChange={handleTextChange('name')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder="e.g. Calm Piano"
              />
            </div>

            <div>
              <label
                htmlFor="audio-url"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Audio URL
              </label>
              <div className="flex gap-2">
                <input
                  id="audio-url"
                  type="text"
                  value={formValues.audio_url}
                  onChange={handleTextChange('audio_url')}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  placeholder="https://example.com/audio.mp3"
                />
                <button
                  type="button"
                  onClick={() => togglePreview(formValues.audio_url)}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-indigo-600 hover:bg-gray-200"
                  title="Test audio URL"
                >
                  {playingPreview === formValues.audio_url ? (
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formValues.is_active}
                onChange={handleActiveChange}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Mark as active (visible on dashboard)
            </label>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button type="button" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="!bg-indigo-500 !text-white hover:!bg-indigo-600 focus:!ring-indigo-400"
              >
                {isSubmitting
                  ? 'Saving…'
                  : activeTrack
                    ? 'Save Changes'
                    : 'Create Track'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {showDeleteModal && activeTrack && (
        <Modal title="Delete Music Track" onClose={closeDeleteModal}>
          <p className="text-sm text-gray-700">
            Are you sure you want to delete{' '}
            <span className="font-semibold">{activeTrack.name}</span>? This
            action cannot be undone.
          </p>
          {deleteError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {deleteError}
            </div>
          )}
          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AdminMusic;
